import { useState } from 'react'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { LuX, LuSearch, LuRotateCcw } from 'react-icons/lu'
import CustomHeroSection from '#/components/common/CustomHeroSection'
import CoursesFilter from '#/features/courses/components/CoursesFilter'
import CoursesFilterDrawer from '#/features/courses/components/CoursesFilterDrawer'
import CoursesList from '#/features/courses/components/CoursesList'
import { useCategories } from '#/features/courses/hooks/useCategories'
import { useTags } from '#/features/courses/hooks/useTags'
import type { CoursesSearch } from '#/routes/_home/courses/index'
import PageTransition from '#/components/common/PageTransition'
import { ScrollReveal, RevealItem } from '#/components/common/ScrollReveal'

const AllCoursesPage = () => {
  const search = (useSearch({ strict: false }) || {}) as CoursesSearch
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data: rawCategories } = useCategories()
  const { data: rawTags } = useTags()
  const categories = Array.isArray(rawCategories) ? rawCategories : []
  const tags = Array.isArray(rawTags) ? rawTags : []

  const currentPage = search.page || 1
  const selectedCategory = search.category
  const selectedTag = search.tag
  const searchQuery = search.search || ''

  const activeCategoryObj = categories.find((c) => c.slug === selectedCategory)
  const activeTagObj = tags.find((t) => t.slug === selectedTag)

  const activeFilterCount =
    (selectedCategory ? 1 : 0) + (selectedTag ? 1 : 0) + (searchQuery ? 1 : 0)

  const handleCategoryChange = (categorySlug?: string) => {
    navigate({
      to: '/courses',
      search: {
        ...search,
        category: categorySlug || undefined,
        page: 1,
      },
    })
  }

  const handleTagChange = (tagSlug?: string) => {
    navigate({
      to: '/courses',
      search: {
        ...search,
        tag: tagSlug || undefined,
        page: 1,
      },
    })
  }

  const handleSearchChange = (query: string) => {
    navigate({
      to: '/courses',
      search: {
        ...search,
        search: query.trim() ? query : undefined,
        page: 1,
      },
    })
  }

  const handleClearAll = () => {
    navigate({
      to: '/courses',
      search: {
        page: 1,
      },
    })
  }

  const handlePageChange = (pageZeroIndexed: number) => {
    const targetPage = pageZeroIndexed + 1
    navigate({
      to: '/courses',
      search: {
        ...search,
        page: targetPage,
      },
    })
  }

  return (
    <PageTransition>
      <main className="flex relative flex-col bg-primary-50/40 min-h-screen">
        <CustomHeroSection title="All Courses" breadcrumbLabel="Courses" />

        <section className="w-full py-10 sm:py-14">
          <div className="container mx-auto px-4">
            {/* ── Responsive Top Filter Bar (Visible on Mobile / Tablet < xl) ── */}
            <div className="mb-6 flex flex-col gap-3 xl:hidden">
              <div className="flex items-center gap-2.5">
                {/* Search input */}
                <div className="relative flex-1">
                  <label className="flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-600 shadow-xs focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
                    <LuSearch className="h-4 w-4 text-neutral-400 shrink-0" />
                    <input
                      className="w-full bg-transparent text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                      placeholder="Search courses..."
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => handleSearchChange('')}
                        className="text-neutral-400 hover:text-neutral-600 p-0.5"
                      >
                        <LuX className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </label>
                </div>

                {/* Drawer trigger button */}
                <CoursesFilterDrawer
                  open={drawerOpen}
                  onOpenChange={setDrawerOpen}
                  selectedCategory={selectedCategory}
                  onSelectCategory={handleCategoryChange}
                  selectedTag={selectedTag}
                  onSelectTag={handleTagChange}
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  activeFilterCount={activeFilterCount}
                  onClearAll={handleClearAll}
                />
              </div>

              {/* Active filter badges row on mobile */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-xs font-medium text-neutral-500">
                    Active:
                  </span>

                  {selectedCategory && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100/80 px-3 py-1 text-xs font-semibold text-primary-800 border border-primary-300/60">
                      Category: {activeCategoryObj?.name || selectedCategory}
                      <button
                        type="button"
                        onClick={() => handleCategoryChange(undefined)}
                        className="rounded-full p-0.5 hover:bg-primary-200 text-primary-700"
                      >
                        <LuX className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {selectedTag && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100/80 px-3 py-1 text-xs font-semibold text-secondary-800 border border-secondary-300/60">
                      Tag: {activeTagObj?.name || selectedTag}
                      <button
                        type="button"
                        onClick={() => handleTagChange(undefined)}
                        className="rounded-full p-0.5 hover:bg-secondary-200 text-secondary-700"
                      >
                        <LuX className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {searchQuery && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-200/80 px-3 py-1 text-xs font-semibold text-neutral-800 border border-neutral-300">
                      "{searchQuery}"
                      <button
                        type="button"
                        onClick={() => handleSearchChange('')}
                        className="rounded-full p-0.5 hover:bg-neutral-300 text-neutral-700"
                      >
                        <LuX className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-xs font-semibold text-neutral-400 hover:text-red-600 underline underline-offset-2 ml-1"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* ── Main Grid (Sidebar on Desktop xl, List on all) ── */}
            <ScrollReveal
              stagger={0.1}
              className="grid grid-cols-1 gap-8 xl:grid-cols-[320px_minmax(0,1fr)]"
            >
              {/* Desktop Sidebar */}
              <RevealItem direction="down" offset={16} scale={0.99}>
                <aside className="hidden xl:block">
                  <div className="sticky top-28">
                    <CoursesFilter
                      selectedCategory={selectedCategory}
                      onSelectCategory={handleCategoryChange}
                      selectedTag={selectedTag}
                      onSelectTag={handleTagChange}
                      searchQuery={searchQuery}
                      onSearchChange={handleSearchChange}
                      variant="sidebar"
                    />
                  </div>
                </aside>
              </RevealItem>

              {/* Courses List Container */}
              <RevealItem direction="down" offset={16} scale={0.99}>
                <div className="flex flex-col gap-6">
                  {/* Desktop Active Filters Pill Bar */}
                  {activeFilterCount > 0 && (
                    <div className="hidden xl:flex items-center justify-between rounded-2xl border border-neutral-200/80 bg-white px-5 py-3 shadow-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mr-1">
                          Active Filters:
                        </span>
                        {selectedCategory && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-800 border border-primary-200">
                            Category:{' '}
                            {activeCategoryObj?.name || selectedCategory}
                            <button
                              type="button"
                              onClick={() => handleCategoryChange(undefined)}
                              className="rounded-full p-0.5 hover:bg-primary-200 text-primary-700"
                            >
                              <LuX className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                        {selectedTag && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-3 py-1 text-xs font-semibold text-secondary-800 border border-secondary-200">
                            Tag: {activeTagObj?.name || selectedTag}
                            <button
                              type="button"
                              onClick={() => handleTagChange(undefined)}
                              className="rounded-full p-0.5 hover:bg-secondary-200 text-secondary-700"
                            >
                              <LuX className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                        {searchQuery && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-800 border border-neutral-200">
                            "{searchQuery}"
                            <button
                              type="button"
                              onClick={() => handleSearchChange('')}
                              className="rounded-full p-0.5 hover:bg-neutral-200 text-neutral-700"
                            >
                              <LuX className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <LuRotateCcw className="h-3 w-3" />
                        Clear all
                      </button>
                    </div>
                  )}

                  <CoursesList
                    page={currentPage}
                    categorySlug={selectedCategory}
                    selectedTag={selectedTag}
                    searchQuery={searchQuery}
                    onPageChange={handlePageChange}
                  />
                </div>
              </RevealItem>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </PageTransition>
  )
}

export default AllCoursesPage
